// Wrappers para /api/classrooms/*.

import { apiFetch } from './api';

export interface TodayClass {
  booking_id: number;
  type: 'one' | 'gr';
  scheduled_at: string;
  duration_min: number;
  booking_status: 'scheduled' | 'cancelled' | 'completed' | 'no_show';
  teacher_id: number;
  teacher_name: string;
  classroom_id: number | null;
  classroom_state: 'open' | 'closed' | null;
}

export interface Booking {
  id: number;
  type: 'one' | 'gr';
  teacher_id: number;
  cohort_id: number | null;
  scheduled_at: string;
  duration_min: number;
  status: 'scheduled' | 'cancelled' | 'completed' | 'no_show';
}

export interface Classroom {
  id: number;
  booking_id: number;
  channel_name: string;
  state: 'open' | 'closed';
  opened_at: string;
  closed_at: string | null;
  opened_by: number;
  closed_by: number | null;
}

export interface Attendance {
  booking_id: number;
  student_id: number;
  display_name: string;
  email: string;
  joined_at: string | null;
  left_at: string | null;
  tracking_status_id: number | null;
  tracking_status_slug: string | null;
}

export interface ChatMessage {
  id: number;
  classroom_id: number;
  user_id: number;
  user_display_name: string;
  content: string;
  created_at: string;
}

export interface AgoraToken {
  appId: string;
  channelName: string;
  uid: number;
  token: string;
  expiresAt: number;
  mock: boolean;
}

export async function listTodayClasses(): Promise<{ items: TodayClass[]; role: string }> {
  const r = await apiFetch<{ status: 'ok'; items: TodayClass[]; role: string }>('/classrooms/today');
  return { items: r.items, role: r.role };
}

export interface FromBookingPayload {
  booking: Booking;
  classroom: Classroom | null;
  attendance: Attendance[];
}

export async function getClassroomFromBooking(bookingId: number): Promise<FromBookingPayload> {
  const r = await apiFetch<{ status: 'ok' } & FromBookingPayload>(`/classrooms/from-booking/${bookingId}`);
  return { booking: r.booking, classroom: r.classroom, attendance: r.attendance };
}

export async function getClassroomDetail(id: number): Promise<{ classroom: Classroom; booking: Booking; attendance: Attendance[] }> {
  const r = await apiFetch<{ status: 'ok'; classroom: Classroom; booking: Booking; attendance: Attendance[] }>(`/classrooms/${id}`);
  return { classroom: r.classroom, booking: r.booking, attendance: r.attendance };
}

export async function openClassroom(bookingId: number): Promise<Classroom> {
  const r = await apiFetch<{ status: 'ok'; classroom: Classroom }>(`/classrooms/from-booking/${bookingId}/open`, { method: 'POST' });
  return r.classroom;
}

export async function closeClassroom(id: number): Promise<void> {
  await apiFetch(`/classrooms/${id}/close`, { method: 'POST' });
}

export async function joinClassroom(id: number): Promise<AgoraToken> {
  const r = await apiFetch<{ status: 'ok'; agora: AgoraToken }>(`/classrooms/${id}/join`, { method: 'POST' });
  return r.agora;
}

export async function leaveClassroom(id: number): Promise<void> {
  await apiFetch(`/classrooms/${id}/leave`, { method: 'POST' });
}

export async function setAttendance(classroomId: number, userId: number, trackingStatusId: number): Promise<void> {
  await apiFetch(`/classrooms/${classroomId}/attendance/${userId}`, {
    method: 'POST',
    body: JSON.stringify({ tracking_status_id: trackingStatusId }),
  });
}

export async function listMessages(classroomId: number, sinceId?: number): Promise<ChatMessage[]> {
  const q = sinceId !== undefined ? `?since=${sinceId}` : '';
  const r = await apiFetch<{ status: 'ok'; messages: ChatMessage[] }>(`/classrooms/${classroomId}/messages${q}`);
  return r.messages;
}

export async function sendMessage(classroomId: number, content: string): Promise<number> {
  const r = await apiFetch<{ status: 'ok'; id: number }>(`/classrooms/${classroomId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ content }),
  });
  return r.id;
}
